import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import supabase from "../../supabase";
import { formatCurrencyManual } from "../../utils/formatters";
import { FormCard } from "../../components/ui/form";
import { Num } from "../../components/ui";
import { tokens } from "../../theme";
import { ticketObjectStatus } from "../../constants/ticketStatus";

/** Líneas en ARS: factor 1. En USD (u otra moneda distinta de ARS): total × cotización de la orden asociada al ticket. */
const currencyMultiplierForLine = (detail, orderCurrencyRate) => {
  const isArs = detail.currency === 1 || detail.currency === "1";
  if (isArs) return 1;
  const rate = parseFloat(orderCurrencyRate);
  return !isNaN(rate) && rate > 0 ? rate : 1;
};

const DetailPayment = ({
  tickets,
  ticketDetails = [],
  setPaymentDetails,
  setTotalExpenses,
  setTotalExpensesWithIva,
  setTotalIva210,
  setTotalIva105,
  disableEdition,
  setDeletePrevColumnsId,
  client,
  selectedTickets,
  setSelectedTickets,
  ticket,
}) => {
  const [ticketsList, setTicketsList] = useState([]);
  const [paymentDetails, setPaymentDetailsState] = useState([]);

  useEffect(() => {
    const fetchTickets = async () => {
      const { data, error } = await supabase
        .from('tickets')
        .select('ticketId, ticketType, ticketNumber, createdAt')
        .eq('clientId', client.clientId)
        .eq('status', ticketObjectStatus.autorizada);

      if (error) {
        console.error('Error al obtener los tickets del cliente:', error);
        return;
      }

      if (ticket && Object.keys(ticket).length > 0) {
        tickets = data.filter(detail => detail.ticketId === ticket.ticketId)
          .map(detail => ({
            ticketId: detail.ticketId,
            ticketType: detail.ticketType,
            ticketNumber: detail.ticketNumber,
            ticketDate: detail.createdAt,
          }));
        setSelectedTickets([ticket.ticketId]);
      } else {
        tickets = data.map(detail => ({
          ticketId: detail.ticketId,
          ticketType: detail.ticketType,
          ticketNumber: detail.ticketNumber,
          ticketDate: detail.createdAt,
        }));
      }
      
      setTicketsList(tickets);

      const fetchTicketDetails = async () => {
        if (!tickets || tickets.length === 0) return;

        const ticketIds = tickets.map((t) => t.ticketId).filter(Boolean);
        const rateByTicketId = new Map();
        if (ticketIds.length > 0) {
          const { data: ordersData, error: ordersError } = await supabase
            .from("orders")
            .select("ticketId, currencyRate")
            .in("ticketId", ticketIds);

          if (ordersError) {
            console.error("Error al obtener cotización de órdenes:", ordersError);
          } else {
            (ordersData || []).forEach((o) => {
              if (o.ticketId == null) return;
              const r = parseFloat(o.currencyRate);
              rateByTicketId.set(o.ticketId, !isNaN(r) && r > 0 ? r : 1);
            });
          }
        }

        const ticketDetails = await Promise.all(
          tickets.map(async (ticketRow) => {
            const currencyRate = rateByTicketId.get(ticketRow.ticketId) ?? 1;

            const { data, error } = await supabase
              .from("ticketDetails")
              .select("quantity, price, ivaPercentage, currency")
              .eq("ticketId", ticketRow.ticketId);

            if (error) {
              console.error("Error al obtener los detalles del ticket:", error);
              return;
            }

            const total = data.reduce((acc, detail) => {
              const mult = currencyMultiplierForLine(detail, currencyRate);
              return (
                acc +
                detail.price *
                  detail.quantity *
                  (1 + detail.ivaPercentage / 100) *
                  mult
              );
            }, 0);

            const subTotal = data.reduce((acc, detail) => {
              const mult = currencyMultiplierForLine(detail, currencyRate);
              return acc + detail.price * detail.quantity * mult;
            }, 0);

            const iva105 = data.reduce((acc, detail) => {
              if (detail.ivaPercentage === 10.5) {
                const mult = currencyMultiplierForLine(detail, currencyRate);
                return (
                  acc +
                  detail.price *
                    detail.quantity *
                    (detail.ivaPercentage / 100) *
                    mult
                );
              }
              return acc;
            }, 0);

            const iva210 = data.reduce((acc, detail) => {
              if (detail.ivaPercentage === 21) {
                const mult = currencyMultiplierForLine(detail, currencyRate);
                return (
                  acc +
                  detail.price *
                    detail.quantity *
                    (detail.ivaPercentage / 100) *
                    mult
                );
              }
              return acc;
            }, 0);

            return {
              ...ticketRow,
              currencyRate,
              total,
              subTotal,
              iva105,
              iva210,
            };
          })
        );
        
        setPaymentDetailsState(ticketDetails);
        setPaymentDetails(ticketDetails);

        // Si hay un ticket único, calcular los totales
        if (ticket && Object.keys(ticket).length > 0) {
          const ticketData = ticketDetails.find(t => t.ticketId === ticket.ticketId);
          if (ticketData) {
            setTotalExpensesWithIva(ticketData.total || 0);
            setTotalExpenses(ticketData.subTotal || 0);
            setTotalIva105(ticketData.iva105 || 0);
            setTotalIva210(ticketData.iva210 || 0);
          }
        }
      };

      fetchTicketDetails();
    };

    fetchTickets();
  }, [ticketDetails, client.clientId]);

  const handleSelectTicket = (ticketId) => {
    const isSelected = selectedTickets.includes(ticketId);
    const updatedSelectedTickets = isSelected ? selectedTickets.filter(id => id !== ticketId) : [...selectedTickets, ticketId];
    setSelectedTickets(updatedSelectedTickets);

    const totalExpensesWithIva = paymentDetails.reduce((acc, detail) => {
      if (updatedSelectedTickets.includes(detail.ticketId)) {
        return acc + (detail.total || 0);
      }
      return acc;
    }, 0);

    const totalExpenses = paymentDetails.reduce((acc, detail) => {
      if (updatedSelectedTickets.includes(detail.ticketId)) {
        return acc + (detail.subTotal || 0);
      }
      return acc;
    }, 0);

    const totalIva105 = paymentDetails.reduce((acc, detail) => {
      if (updatedSelectedTickets.includes(detail.ticketId)) {
        return acc + (detail.iva105 || 0);
      }
      return acc;
    }, 0);

    const totalIva210 = paymentDetails.reduce((acc, detail) => {
      if (updatedSelectedTickets.includes(detail.ticketId)) {
        return acc + (detail.iva210 || 0);      }
      return acc;
    }, 0);

    setTotalExpensesWithIva(totalExpensesWithIva);
    setTotalExpenses(totalExpenses);
    setTotalIva105(totalIva105);
    setTotalIva210(totalIva210);
  };

  const total = paymentDetails.reduce(
    (acc, t) => acc + (selectedTickets.includes(t.ticketId) ? (t.total || 0) : 0),
    0
  );

  return (
    <FormCard
      title="Facturas"
      subtitle={
        paymentDetails.length === 0
          ? "Este cliente no tiene facturas pendientes"
          : `${selectedTickets.length} de ${paymentDetails.length} seleccionadas · ${formatCurrencyManual(total)}`
      }
      sx={{ width: "100%", minWidth: 0 }}
    >
      {paymentDetails.length === 0 ? (
        <Typography sx={{ fontSize: "13px", color: tokens.ink3, padding: "8px 0" }}>
          No hay facturas para mostrar.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: 0 }}>
          {/* Encabezado sólo en escritorio: en teléfono cada factura es una ficha. */}
          <Box
            sx={{
              display: { xs: "none", md: "grid" },
              gridTemplateColumns: "28px 70px 110px minmax(0, 1fr) 130px",
              gap: "12px",
              padding: "0 12px 6px",
              borderBottom: `1px solid ${tokens.border}`,
            }}
          >
            {["", "ID", "Fecha", "Descripción", "Monto"].map((h, i) => (
              <Typography
                key={h || i}
                sx={{
                  fontSize: "10.5px",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: tokens.ink3,
                  textAlign: i === 4 ? "right" : "left",
                }}
              >
                {h}
              </Typography>
            ))}
          </Box>

          {paymentDetails.map((ticket, index) => {
            const seleccionada = selectedTickets.includes(ticket.ticketId);
            return (
              <Box
                key={index}
                component="label"
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "28px minmax(0, 1fr)", md: "28px 70px 110px minmax(0, 1fr) 130px" },
                  alignItems: { xs: "start", md: "center" },
                  gap: { xs: "10px", md: "12px" },
                  padding: { xs: "11px 12px", md: "9px 12px" },
                  minHeight: 44,
                  cursor: disableEdition ? "default" : "pointer",
                  borderRadius: `${tokens.radiusControl}px`,
                  border: `1px solid ${seleccionada ? tokens.brandBorder : tokens.border}`,
                  backgroundColor: seleccionada ? tokens.brandTint : tokens.surface,
                  "&:hover": { borderColor: seleccionada ? tokens.brandBorder : tokens.borderStrong },
                }}
              >
                <Box
                  component="input"
                  type="checkbox"
                  disabled={disableEdition}
                  onChange={() => handleSelectTicket(ticket.ticketId)}
                  checked={seleccionada}
                  sx={{ width: 17, height: 17, accentColor: tokens.brand, cursor: "inherit", margin: 0 }}
                />

                {/* Teléfono: todo apilado en una ficha. Escritorio: celdas en fila. */}
                <Box
                  sx={{
                    display: { xs: "flex", md: "contents" },
                    flexDirection: "column",
                    gap: "3px",
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ display: { xs: "flex", md: "block" }, gap: "8px", alignItems: "baseline" }}>
                    <Num sx={{ fontSize: "13px", fontWeight: 600 }}>#{ticket.ticketId}</Num>
                    <Typography sx={{ display: { xs: "block", md: "none" }, fontSize: "12px", color: tokens.ink3 }}>
                      {ticket.ticketDate ? new Date(ticket.ticketDate).toLocaleDateString() : "Sin fecha"}
                    </Typography>
                  </Box>

                  <Num sx={{ display: { xs: "none", md: "block" }, fontSize: "13px", color: tokens.ink2 }}>
                    {ticket.ticketDate ? new Date(ticket.ticketDate).toLocaleDateString() : "Sin fecha"}
                  </Num>

                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: tokens.ink2,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: { xs: "normal", md: "nowrap" },
                    }}
                  >
                    {ticket.ticketType} Nº {ticket.ticketNumber}
                  </Typography>

                  <Num
                    sx={{
                      fontSize: { xs: "14px", md: "13.5px" },
                      fontWeight: 600,
                      textAlign: { xs: "left", md: "right" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatCurrencyManual(ticket.total)}
                  </Num>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </FormCard>
  );
};

export default DetailPayment;